/**
 * Journal-Bewertungen 1-10 mit Kategorien (Roadmap V-09).
 *
 * Skala 1-10, 10 = gut (Leitlinie 6). Die Kategorien sind Daten, keine
 * festen Felder: man kann eigene hinzufuegen, umbenennen oder ausblenden,
 * ohne dass alte Werte verloren gehen. Die Standards haben feste IDs, damit
 * die Umrechnung alter Werte sie sicher trifft.
 */
import { addDays } from '@/lib/datetime';
import type { ID, JournalCategory, JournalEntry, LocalDate } from '@/domain/types';

type Standard = Pick<JournalCategory, 'id' | 'name' | 'journal' | 'hint'>;

export const STANDARD_KATEGORIEN: Standard[] = [
  { id: 'm-schlaf', name: 'Schlafqualität', journal: 'morgen' },
  { id: 'm-energie', name: 'Energie', journal: 'morgen' },
  { id: 'm-stimmung', name: 'Stimmung', journal: 'morgen' },
  { id: 'm-motivation', name: 'Motivation', journal: 'morgen' },
  { id: 'a-energie', name: 'Energie', journal: 'abend' },
  { id: 'a-stimmung', name: 'Stimmung', journal: 'abend' },
  { id: 'a-fokus', name: 'Fokus', journal: 'abend', hint: 'Konzentration' },
  { id: 'a-produktivitaet', name: 'Produktivität', journal: 'abend' },
  { id: 'a-gelassenheit', name: 'Gelassenheit', journal: 'abend', hint: '10 = entspannt, 1 = sehr gestresst' },
  { id: 'a-koerper', name: 'Körperliches Befinden', journal: 'abend' },
  // Reflexionen (V-11): wie der Zeitraum gelaufen ist - nicht die
  // Lebensbereiche, die gehoeren ins Lebensrad.
  ...reflexionsKategorien('w', 'woche', 'der Woche'),
  ...reflexionsKategorien('mo', 'monat', 'dem Monat'),
];

function reflexionsKategorien(p: string, journal: 'woche' | 'monat', bezug: string): Standard[] {
  return [
    { id: `${p}-zufriedenheit`, name: `Zufriedenheit mit ${bezug}`, journal },
    { id: `${p}-produktivitaet`, name: 'Produktivität', journal },
    { id: `${p}-energie`, name: 'Energie', journal },
    { id: `${p}-stimmung`, name: 'Stimmung', journal },
    { id: `${p}-ziele`, name: 'Fortschritt bei meinen Zielen', journal },
    { id: `${p}-disziplin`, name: 'Disziplin', journal, hint: 'Gewohnheiten und Vorsätze eingehalten' },
    { id: `${p}-erholung`, name: 'Erholung', journal, hint: 'Ausgleich' },
  ];
}

export function standardKategorien(ts: string): JournalCategory[] {
  return STANDARD_KATEGORIEN.map((k, i) => ({ ...k, order: i, hidden: false, createdAt: ts, updatedAt: ts }));
}

/** Alte 1-5-Werte mal 2 (1 -> 2 ... 5 -> 10), wie in der Roadmap festgelegt. */
export function auf10(wert5: number): number {
  return Math.min(10, Math.max(1, Math.round(wert5 * 2)));
}

type Zeile = Partial<Omit<JournalEntry, 'type'>> & { id: string; type: string };

/**
 * Stellt alte Eintraege auf 1-10 um: Stimmung, Energie und Schlafqualitaet
 * werden zu Bewertungen der passenden Standardkategorie (x2); die
 * Originalwerte bleiben unter `legacy5` erhalten. Schon umgestellte Eintraege
 * bleiben unveraendert. Rein - fuer Datenbank-Upgrade und Sicherungen.
 */
export function bewertungenAuf10<T extends Zeile>(zeilen: T[]): T[] {
  return zeilen.map((z) => {
    if (z.mood === undefined && z.energy === undefined && z.sleepQuality === undefined) return z;
    const neu = { ...z };
    const ratings: Record<ID, number> = { ...(z.ratings ?? {}) };
    const p = z.type === 'morgen' ? 'm' : z.type === 'abend' ? 'a' : null;
    if (p) {
      if (z.mood !== undefined) ratings[`${p}-stimmung`] ??= auf10(z.mood);
      if (z.energy !== undefined) ratings[`${p}-energie`] ??= auf10(z.energy);
    }
    if (z.sleepQuality !== undefined && z.type === 'morgen') ratings['m-schlaf'] ??= auf10(z.sleepQuality);
    neu.legacy5 = {
      ...(z.mood !== undefined ? { mood: z.mood } : {}),
      ...(z.energy !== undefined ? { energy: z.energy } : {}),
      ...(z.sleepQuality !== undefined ? { sleepQuality: z.sleepQuality } : {}),
    };
    if (Object.keys(ratings).length > 0) neu.ratings = ratings;
    delete neu.mood;
    delete neu.energy;
    delete neu.sleepQuality;
    return neu;
  });
}

/**
 * Tageswert einer Kategorie. Jede Kategorie gehoert zu einem Journal, es gibt
 * also hoechstens einen Wert pro Tag.
 */
export function tageswerte(eintraege: JournalEntry[], kategorie: JournalCategory): Map<LocalDate, number> {
  const map = new Map<LocalDate, number>();
  for (const e of eintraege) {
    if (e.type !== kategorie.journal) continue;
    const w = e.ratings?.[kategorie.id];
    if (w !== undefined) map.set(e.date, w);
  }
  return map;
}

/** Mittel der letzten 7 Tage bis einschliesslich `heute`, `null` ohne Werte. */
export function schnitt7(werte: Map<LocalDate, number>, heute: LocalDate): number | null {
  const liste: number[] = [];
  for (let i = 0; i < 7; i += 1) {
    const w = werte.get(addDays(heute, -i));
    if (w !== undefined) liste.push(w);
  }
  return liste.length === 0 ? null : liste.reduce((a, b) => a + b, 0) / liste.length;
}

/** Letzter Tag mit umgerechneten 1-5-Werten - ab dort sind die Werte feiner. */
export function umstellungBis(eintraege: JournalEntry[]): LocalDate | null {
  let letzter: LocalDate | null = null;
  for (const e of eintraege) if (e.legacy5 && (!letzter || e.date > letzter)) letzter = e.date;
  return letzter;
}

/**
 * Durchschnitt der Journal-Werte eines Zeitraums fuer eine Reflexions-
 * kategorie (V-11, Vorschlag): alle Journal-Kategorien mit demselben Namen
 * (z. B. "Energie" morgens und abends), `null` ohne Werte. So steht das
 * Gefuehl im Rueckblick neben den tatsaechlichen Tageswerten.
 */
export function journalSchnitt(
  name: string,
  eintraege: JournalEntry[],
  kategorien: JournalCategory[],
  von: LocalDate,
  bis: LocalDate,
): number | null {
  const n = name.trim().toLowerCase();
  const ids = new Set(kategorien
    .filter((k) => (k.journal === 'morgen' || k.journal === 'abend') && k.name.trim().toLowerCase() === n)
    .map((k) => k.id));
  if (ids.size === 0) return null;
  const werte: number[] = [];
  for (const e of eintraege) {
    if (e.date < von || e.date > bis) continue;
    for (const [id, w] of Object.entries(e.ratings ?? {})) if (ids.has(id)) werte.push(w);
  }
  return werte.length === 0 ? null : werte.reduce((a, b) => a + b, 0) / werte.length;
}
